/**
 * WithdrawOfferButton - The "Withdraw offer" button and its confirmation.
 *
 * The caller owns the request: `onConfirm` runs when the investor confirms, and
 * `isWithdrawing` disables the button while the caller's request is in flight.
 */

import { REDACT_ATTRS } from '@/common/analytics'
import { Button } from '@/common/components/button'
import { AlertDialog } from '@/common/components/alert-dialog'
import * as styles from './WithdrawOfferButton.css'

export interface WithdrawOfferButtonProps {
  /** The offer's price, named in the confirmation. */
  offerPrice: number
  isWithdrawing: boolean
  onConfirm: () => void
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

export function WithdrawOfferButton({ offerPrice, isWithdrawing, onConfirm }: WithdrawOfferButtonProps) {
  return (
    <AlertDialog>
      <AlertDialog.Trigger asChild>
        <Button
          variant="outline"
          size="sm"
          width="full"
          disabled={isWithdrawing}
          className={styles.withdrawButton}
        >
          {isWithdrawing ? 'Withdrawing...' : 'Withdraw offer'}
        </Button>
      </AlertDialog.Trigger>
      {/* The confirmation is portal-rendered, outside any page-level redaction
          baseline (and the partner link page has no signed-in layout at all),
          so it carries its own. Both attributes cascade to the whole dialog,
          which names the offer price. */}
      <AlertDialog.Content
        data-heap-redact-text="true"
        data-heap-redact-attributes={REDACT_ATTRS}
      >
        <AlertDialog.Title>Withdraw Offer</AlertDialog.Title>
        <AlertDialog.Description>
          Are you sure you want to withdraw your offer of {formatCurrency(offerPrice)}? This action cannot be undone.
        </AlertDialog.Description>
        <AlertDialog.Actions>
          <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
          <AlertDialog.Action variant="danger" onClick={onConfirm}>
            Withdraw
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
